import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ContractSection {
  id: string;
  name: string;
  content: string;
  order: number;
}

export interface MergeField {
  key: string;
  label: string;
  type: "text" | "number" | "date" | "currency";
  defaultValue?: string;
}

export interface ContractTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  sections: ContractSection[];
  merge_fields: MergeField[];
  is_default: boolean;
  category: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export function useContractTemplates() {
  return useQuery({
    queryKey: ["contract-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as ContractTemplate[];
    },
  });
}

export function useContractTemplate(templateId: string) {
  return useQuery({
    queryKey: ["contract-templates", templateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("*")
        .eq("id", templateId)
        .single();

      if (error) throw error;
      return data as unknown as ContractTemplate;
    },
    enabled: !!templateId,
  });
}

export function useCreateContractTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (template: {
      name: string;
      description?: string;
      sections: ContractSection[];
      merge_fields: MergeField[];
      category?: string;
      is_default?: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) throw new Error("No tenant assigned");

      const insertData = {
        name: template.name,
        description: template.description,
        category: template.category,
        is_default: template.is_default,
        tenant_id: profile.tenant_id,
        sections: template.sections as unknown as Record<string, unknown>,
        merge_fields: template.merge_fields as unknown as Record<string, unknown>,
      };

      const { data, error } = await supabase
        .from("contract_templates")
        .insert(insertData as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template created successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateContractTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ContractTemplate> & { id: string }) => {
      const updateData: Record<string, unknown> = { ...updates };
      if (updates.sections) {
        updateData.sections = updates.sections as unknown as Record<string, unknown>;
      }
      if (updates.merge_fields) {
        updateData.merge_fields = updates.merge_fields as unknown as Record<string, unknown>;
      }

      const { data, error } = await supabase
        .from("contract_templates")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template updated");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteContractTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contract_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-templates"] });
      toast.success("Template deleted");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
